export const categories = [
  {
    slug: "getting-started",
    title: "Getting Started",
    desc: "Set up your account, upload files, and learn the basics.",
    color: "catBlue",
    articles: [
      {
        id: "uploading-ifc-files",
        title: "How to Upload and View IFC Files",
        category: "Getting Started",
        categorySlug: "getting-started",
        badge: "popular",
        readTime: "5 min",
        lastUpdated: "2026-03-01",
        content: `<h2>Uploading IFC Files</h2>
<p>viewStruct supports IFC 2x3 and IFC 4 file formats. Follow these steps to upload your first model:</p>
<ol>
<li><strong>Navigate to File Manager</strong> – Click "File Manager" in the left sidebar of your dashboard.</li>
<li><strong>Click Upload</strong> – Use the upload button or drag and drop your IFC file directly into the browser window.</li>
<li><strong>Wait for Processing</strong> – The system will parse and convert your IFC model. This typically takes 30 seconds to 5 minutes depending on file size.</li>
<li><strong>View Your Model</strong> – Once processed, click on the file to open it in the 3D viewer.</li>
</ol>
<h3>Supported File Formats</h3>
<ul>
<li>IFC 2x3 (.ifc)</li>
<li>IFC 4 (.ifc)</li>
<li>Revit (.rvt) – requires cloud conversion</li>
<li>DWG (.dwg) – 2D viewing only</li>
<li>PDF (.pdf) – 2D sheet viewing</li>
</ul>
<div class="tip"><strong>Tip:</strong> For best performance, keep individual IFC files under 500MB. Larger models can be split into discipline-specific files.</div>`,
      },
      {
        id: "creating-new-project",
        title: "Creating Your First Project",
        category: "Getting Started",
        categorySlug: "getting-started",
        badge: null,
        readTime: "4 min",
        lastUpdated: "2026-02-20",
        content: `<h2>Creating a New Project</h2>
<p>Projects are the primary way to organize your BIM data in viewStruct. Each project can contain multiple models, documents, and team members.</p>
<ol>
<li>From the Dashboard, click <strong>"New Project"</strong>.</li>
<li>Enter a project name and optional description.</li>
<li>Choose your project template (blank, residential, commercial, infrastructure).</li>
<li>Set the coordinate system and units.</li>
<li>Click <strong>"Create"</strong>.</li>
</ol>
<h3>Project Settings</h3>
<p>After creation, you can configure additional settings:</p>
<ul>
<li><strong>Location</strong> – Set the geographic location for your project on a map.</li>
<li><strong>Phases</strong> – Define construction phases and timelines.</li>
<li><strong>Categories</strong> – Customize issue categories and status workflows.</li>
</ul>`,
      },
      {
        id: "navigating-the-dashboard",
        title: "Navigating the Dashboard",
        category: "Getting Started",
        categorySlug: "getting-started",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-02-15",
        content: `<h2>Dashboard Overview</h2>
<p>The dashboard is your central hub for managing projects, viewing recent activity, and accessing key features.</p>
<h3>Main Sections</h3>
<ul>
<li><strong>Recent Projects</strong> – Quick access to your most recently opened projects.</li>
<li><strong>Activity Feed</strong> – See what your team has been working on.</li>
<li><strong>Quick Actions</strong> – Upload files, create issues, or invite team members.</li>
<li><strong>Storage Usage</strong> – Monitor your storage quota.</li>
</ul>
<h3>Sidebar Navigation</h3>
<p>The left sidebar provides access to all major sections: Dashboard, File Manager, Issues, Storage, and Settings.</p>`,
      },
      {
        id: "inviting-team-members",
        title: "Inviting Team Members",
        category: "Getting Started",
        categorySlug: "getting-started",
        badge: "new",
        readTime: "3 min",
        lastUpdated: "2026-03-05",
        content: `<h2>Adding Team Members</h2>
<p>Collaboration is at the heart of viewStruct. Here's how to invite your team:</p>
<ol>
<li>Open your project and go to <strong>Settings → Team</strong>.</li>
<li>Click <strong>"Invite Members"</strong>.</li>
<li>Enter email addresses (comma-separated for multiple invites).</li>
<li>Select a role: Viewer, Contributor, or Admin.</li>
<li>Click <strong>"Send Invites"</strong>.</li>
</ol>
<h3>Role Permissions</h3>
<ul>
<li><strong>Viewer</strong> – Can view models and documents. Cannot edit or upload.</li>
<li><strong>Contributor</strong> – Can upload files, create issues, and add comments.</li>
<li><strong>Admin</strong> – Full access including project settings and member management.</li>
</ul>`,
      },
      {
        id: "understanding-file-processing",
        title: "Understanding File Processing",
        category: "Getting Started",
        categorySlug: "getting-started",
        badge: null,
        readTime: "4 min",
        lastUpdated: "2026-02-10",
        content: `<h2>How File Processing Works</h2>
<p>When you upload a BIM file, viewStruct processes it through several stages:</p>
<ol>
<li><strong>Upload</strong> – File is securely transferred to our servers.</li>
<li><strong>Validation</strong> – File format and integrity are verified.</li>
<li><strong>Conversion</strong> – The model is converted to our optimized viewing format.</li>
<li><strong>Indexing</strong> – Properties, elements, and spatial data are indexed for search.</li>
<li><strong>Ready</strong> – The model is available for viewing and collaboration.</li>
</ol>
<h3>Processing Times</h3>
<p>Typical processing times based on file size:</p>
<ul>
<li>Under 50MB: 30 seconds – 1 minute</li>
<li>50–200MB: 1–3 minutes</li>
<li>200–500MB: 3–8 minutes</li>
<li>Over 500MB: 8–15 minutes</li>
</ul>`,
      },
      {
        id: "setting-up-notifications",
        title: "Setting Up Notifications",
        category: "Getting Started",
        categorySlug: "getting-started",
        badge: null,
        readTime: "2 min",
        lastUpdated: "2026-01-28",
        content: `<h2>Notification Settings</h2>
<p>Stay up to date with project activity by configuring your notification preferences.</p>
<h3>Notification Types</h3>
<ul>
<li><strong>Email</strong> – Receive digest or instant email notifications.</li>
<li><strong>In-App</strong> – Bell icon notifications within viewStruct.</li>
<li><strong>Push</strong> – Browser push notifications (requires permission).</li>
</ul>
<h3>Configuring Preferences</h3>
<p>Go to <strong>Profile → Preferences → Notifications</strong> to customize which events trigger notifications:</p>
<ul>
<li>New issues assigned to you</li>
<li>Comments on your issues</li>
<li>File uploads in your projects</li>
<li>Team member changes</li>
</ul>`,
      },
      {
        id: "quick-start-guide-for-architects",
        title: "Quick Start Guide for Architects",
        category: "Getting Started",
        categorySlug: "getting-started",
        badge: null,
        readTime: "6 min",
        lastUpdated: "2026-02-01",
        content: `<h2>Architect's Quick Start</h2>
<p>This guide is tailored for architects new to viewStruct, covering the essential workflow from upload to collaboration.</p>
<h3>Step 1: Upload Your Revit/IFC Model</h3>
<p>Export your model as IFC from Revit (File → Export → IFC) and upload it to viewStruct.</p>
<h3>Step 2: Organize with Folders</h3>
<p>Create a folder structure that mirrors your project phases: Schematic Design, Design Development, Construction Documents.</p>
<h3>Step 3: Share with Consultants</h3>
<p>Invite structural and MEP engineers to view and comment on your model.</p>
<h3>Step 4: Track Issues</h3>
<p>Use the Issues module to track design conflicts, RFIs, and coordination notes.</p>`,
      },
      {
        id: "quick-start-guide-for-engineers",
        title: "Quick Start Guide for Engineers",
        category: "Getting Started",
        categorySlug: "getting-started",
        badge: null,
        readTime: "6 min",
        lastUpdated: "2026-02-01",
        content: `<h2>Engineer's Quick Start</h2>
<p>This guide covers the key workflows for structural and MEP engineers using viewStruct.</p>
<h3>Model Coordination</h3>
<p>Use the model comparison tool to overlay architectural and engineering models, identifying clashes early.</p>
<h3>Measurement Tools</h3>
<p>The 3D viewer includes precise measurement tools for distances, areas, and volumes.</p>
<h3>Issue Tracking</h3>
<p>Create issues directly from the 3D view – click on an element, select "Create Issue," and it automatically captures the viewpoint and element data.</p>`,
      },
      {
        id: "system-requirements",
        title: "System Requirements",
        category: "Getting Started",
        categorySlug: "getting-started",
        badge: null,
        readTime: "2 min",
        lastUpdated: "2026-01-15",
        content: `<h2>System Requirements</h2>
<h3>Browser Requirements</h3>
<ul>
<li>Chrome 90+ (recommended)</li>
<li>Firefox 88+</li>
<li>Safari 14+</li>
<li>Edge 90+</li>
</ul>
<h3>Hardware Recommendations</h3>
<ul>
<li><strong>Minimum:</strong> 4GB RAM, integrated graphics</li>
<li><strong>Recommended:</strong> 8GB+ RAM, dedicated GPU with WebGL 2.0 support</li>
<li><strong>Large models:</strong> 16GB+ RAM, modern dedicated GPU</li>
</ul>
<h3>Network</h3>
<p>A stable internet connection of at least 10 Mbps is recommended for smooth 3D viewing. Upload speeds of 5+ Mbps are recommended for file uploads.</p>`,
      },
      {
        id: "keyboard-shortcuts",
        title: "Keyboard Shortcuts",
        category: "Getting Started",
        categorySlug: "getting-started",
        badge: "updated",
        readTime: "3 min",
        lastUpdated: "2026-03-02",
        content: `<h2>Keyboard Shortcuts</h2>
<h3>General</h3>
<ul>
<li><strong>Ctrl + N</strong> – New project</li>
<li><strong>Ctrl + U</strong> – Upload file</li>
<li><strong>Ctrl + K</strong> – Quick search</li>
<li><strong>?</strong> – Show all shortcuts</li>
</ul>
<h3>3D Viewer</h3>
<ul>
<li><strong>F</strong> – Fit model to screen</li>
<li><strong>H</strong> – Home view</li>
<li><strong>I</strong> – Toggle isolate mode</li>
<li><strong>M</strong> – Measurement tool</li>
<li><strong>Esc</strong> – Cancel current operation</li>
</ul>
<h3>Issues</h3>
<ul>
<li><strong>N</strong> – New issue</li>
<li><strong>E</strong> – Edit selected issue</li>
<li><strong>Ctrl + Enter</strong> – Submit comment</li>
</ul>`,
      },
      {
        id: "mobile-app-setup",
        title: "Mobile App Setup",
        category: "Getting Started",
        categorySlug: "getting-started",
        badge: "new",
        readTime: "3 min",
        lastUpdated: "2026-03-04",
        content: `<h2>Mobile App Setup</h2>
<p>viewStruct's mobile app lets you access your projects on the go.</p>
<h3>Installation</h3>
<ol>
<li>Download viewStruct from the App Store (iOS) or Google Play (Android).</li>
<li>Open the app and sign in with your viewStruct credentials.</li>
<li>Enable push notifications for real-time updates.</li>
</ol>
<h3>Mobile Features</h3>
<ul>
<li>View 3D/2D models with touch navigation</li>
<li>Create and comment on issues</li>
<li>Take photos and attach them to issues</li>
<li>Offline access to recently viewed models</li>
</ul>`,
      },
      {
        id: "data-import-from-other-platforms",
        title: "Data Import from Other Platforms",
        category: "Getting Started",
        categorySlug: "getting-started",
        badge: null,
        readTime: "5 min",
        lastUpdated: "2026-02-18",
        content: `<h2>Importing Data</h2>
<p>Migrating from another platform? viewStruct supports importing data from popular BIM collaboration tools.</p>
<h3>Supported Imports</h3>
<ul>
<li><strong>BIM 360</strong> – Projects, files, and issues</li>
<li><strong>Procore</strong> – Documents and RFIs</li>
<li><strong>Trimble Connect</strong> – Models and to-dos</li>
<li><strong>Manual</strong> – CSV import for issues and metadata</li>
</ul>
<h3>Import Steps</h3>
<ol>
<li>Go to <strong>Settings → Import</strong>.</li>
<li>Select the source platform.</li>
<li>Authorize the connection.</li>
<li>Choose which data to import.</li>
<li>Review and confirm the import.</li>
</ol>`,
      },
    ],
  },
  {
    slug: "3d-2d-viewer",
    title: "3D/2D Viewer",
    desc: "Navigate, annotate, and measure models in the viewer.",
    color: "catOrange",
    articles: [
      {
        id: "comparing-model-versions-side-by-side",
        title: "Comparing Model Versions Side by Side",
        category: "3D/2D Viewer",
        categorySlug: "3d-2d-viewer",
        badge: "updated",
        readTime: "5 min",
        lastUpdated: "2026-03-01",
        content: `<h2>Model Comparison</h2>
<p>viewStruct's comparison tool lets you identify changes between model versions instantly.</p>
<h3>How to Compare</h3>
<ol>
<li>Open a model in the 3D viewer.</li>
<li>Click the <strong>"Compare"</strong> button in the toolbar.</li>
<li>Select the version to compare against.</li>
<li>Changes are highlighted: <span style="color:green">green = added</span>, <span style="color:red">red = removed</span>, <span style="color:orange">orange = modified</span>.</li>
</ol>
<h3>Comparison Modes</h3>
<ul>
<li><strong>Side by Side</strong> – Two viewers synced together.</li>
<li><strong>Overlay</strong> – Both versions overlaid with color coding.</li>
<li><strong>Split Screen</strong> – Draggable divider between versions.</li>
</ul>`,
      },
      {
        id: "exporting-annotations-and-markups",
        title: "Exporting Annotations and Markups",
        category: "3D/2D Viewer",
        categorySlug: "3d-2d-viewer",
        badge: "popular",
        readTime: "4 min",
        lastUpdated: "2026-02-25",
        content: `<h2>Exporting Annotations</h2>
<p>All your markups and annotations can be exported for use in other tools or for documentation.</p>
<h3>Export Formats</h3>
<ul>
<li><strong>PDF</strong> – Annotated screenshots with notes.</li>
<li><strong>BCF</strong> – BIM Collaboration Format for issue exchange.</li>
<li><strong>CSV</strong> – Tabular data of all annotations.</li>
<li><strong>PNG/JPG</strong> – Individual annotated screenshots.</li>
</ul>
<h3>Steps to Export</h3>
<ol>
<li>Open the Annotations panel.</li>
<li>Select annotations to export (or select all).</li>
<li>Click <strong>"Export"</strong> and choose your format.</li>
<li>Download the generated file.</li>
</ol>`,
      },
      {
        id: "using-measurement-tools",
        title: "Using Measurement Tools",
        category: "3D/2D Viewer",
        categorySlug: "3d-2d-viewer",
        badge: null,
        readTime: "4 min",
        lastUpdated: "2026-02-20",
        content: `<h2>Measurement Tools</h2>
<p>Precise measurements directly in the 3D/2D viewer.</p>
<h3>Available Measurements</h3>
<ul>
<li><strong>Point to Point</strong> – Distance between two points.</li>
<li><strong>Area</strong> – Surface area of selected regions.</li>
<li><strong>Angle</strong> – Angle between two lines or surfaces.</li>
<li><strong>Volume</strong> – Volume of enclosed spaces (3D only).</li>
</ul>
<h3>Using Measurements</h3>
<ol>
<li>Press <strong>M</strong> or click the ruler icon.</li>
<li>Select measurement type.</li>
<li>Click on the model to place measurement points.</li>
<li>Results appear in real-time with snap-to-edge precision.</li>
</ol>`,
      },
      {
        id: "creating-and-managing-viewpoints",
        title: "Creating and Managing Viewpoints",
        category: "3D/2D Viewer",
        categorySlug: "3d-2d-viewer",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-02-15",
        content: `<h2>Viewpoints</h2>
<p>Save specific camera positions and visibility settings as viewpoints for easy navigation.</p>
<h3>Creating a Viewpoint</h3>
<ol>
<li>Navigate to the desired view.</li>
<li>Adjust element visibility (hide/isolate as needed).</li>
<li>Click the camera icon → <strong>"Save Viewpoint"</strong>.</li>
<li>Name your viewpoint and add an optional description.</li>
</ol>
<h3>Using Viewpoints</h3>
<p>Viewpoints appear in the sidebar panel. Click any viewpoint to instantly navigate to that saved view. Viewpoints can also be attached to issues for precise context.</p>`,
      },
      {
        id: "sectioning-and-clipping-planes",
        title: "Sectioning and Clipping Planes",
        category: "3D/2D Viewer",
        categorySlug: "3d-2d-viewer",
        badge: null,
        readTime: "4 min",
        lastUpdated: "2026-02-10",
        content: `<h2>Section Planes</h2>
<p>Cut through your model to see internal details using clipping planes.</p>
<h3>Adding a Section Plane</h3>
<ol>
<li>Click the <strong>Section</strong> tool in the viewer toolbar.</li>
<li>Choose the plane orientation (X, Y, or Z axis).</li>
<li>Drag the plane to position it.</li>
<li>Use the handles to rotate or flip the section.</li>
</ol>
<h3>Multiple Sections</h3>
<p>You can add up to 6 section planes simultaneously to create section boxes, isolating specific areas of your model.</p>`,
      },
      {
        id: "element-properties-and-data",
        title: "Element Properties and Data",
        category: "3D/2D Viewer",
        categorySlug: "3d-2d-viewer",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-02-08",
        content: `<h2>Viewing Element Properties</h2>
<p>Click on any element in the 3D model to view its full property set.</p>
<h3>Property Panel</h3>
<ul>
<li><strong>Identity</strong> – Name, type, GUID, and classification.</li>
<li><strong>Dimensions</strong> – Width, height, length, area, volume.</li>
<li><strong>Materials</strong> – Assigned materials and finishes.</li>
<li><strong>Custom Properties</strong> – Any IFC property sets defined in the authoring tool.</li>
</ul>
<h3>Search by Property</h3>
<p>Use the property search to find elements by any property value across the entire model.</p>`,
      },
      {
        id: "hiding-and-isolating-elements",
        title: "Hiding and Isolating Elements",
        category: "3D/2D Viewer",
        categorySlug: "3d-2d-viewer",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-02-05",
        content: `<h2>Visibility Controls</h2>
<p>Control element visibility to focus on specific parts of your model.</p>
<h3>Hide Elements</h3>
<p>Right-click an element and select <strong>"Hide"</strong> to make it transparent. Use the model tree to hide entire categories (walls, floors, etc.).</p>
<h3>Isolate Elements</h3>
<p>Select elements and press <strong>I</strong> to isolate them, hiding everything else. This is great for focusing on specific systems.</p>
<h3>Ghosting</h3>
<p>Enable ghost mode to make hidden elements semi-transparent rather than fully invisible, maintaining spatial context.</p>`,
      },
      {
        id: "walk-through-mode",
        title: "Walk-Through Mode",
        category: "3D/2D Viewer",
        categorySlug: "3d-2d-viewer",
        badge: "new",
        readTime: "3 min",
        lastUpdated: "2026-03-03",
        content: `<h2>Walk-Through Mode</h2>
<p>Experience your model from a first-person perspective.</p>
<h3>Controls</h3>
<ul>
<li><strong>WASD</strong> – Move forward, left, backward, right.</li>
<li><strong>Mouse</strong> – Look around.</li>
<li><strong>Space</strong> – Move up.</li>
<li><strong>Shift</strong> – Move down.</li>
<li><strong>Scroll</strong> – Adjust movement speed.</li>
</ul>
<h3>Settings</h3>
<p>Adjust eye height, collision detection, and movement speed in the walk-through settings panel.</p>`,
      },
      {
        id: "2d-sheet-viewing-and-markup",
        title: "2D Sheet Viewing and Markup",
        category: "3D/2D Viewer",
        categorySlug: "3d-2d-viewer",
        badge: null,
        readTime: "4 min",
        lastUpdated: "2026-02-12",
        content: `<h2>2D Sheet Viewer</h2>
<p>View and annotate 2D drawings and PDF sheets within viewStruct.</p>
<h3>Supported Formats</h3>
<ul><li>PDF</li><li>DWG (converted)</li><li>DXF</li></ul>
<h3>Markup Tools</h3>
<ul>
<li>Pen / Highlighter</li>
<li>Shapes (rectangle, circle, arrow)</li>
<li>Text annotations</li>
<li>Dimension markups</li>
<li>Cloud / revision markers</li>
</ul>`,
      },
      {
        id: "model-tree-navigation",
        title: "Model Tree Navigation",
        category: "3D/2D Viewer",
        categorySlug: "3d-2d-viewer",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-02-01",
        content: `<h2>Model Tree</h2>
<p>The model tree provides a hierarchical view of all elements in your model.</p>
<h3>Navigation</h3>
<ul>
<li>Click a node to select and zoom to the element.</li>
<li>Use checkboxes to toggle visibility.</li>
<li>Right-click for context menu options (isolate, hide, properties).</li>
</ul>
<h3>Filtering</h3>
<p>Filter the model tree by category, type, or search term to quickly find elements.</p>`,
      },
      {
        id: "color-coding-by-property",
        title: "Color-Coding by Property",
        category: "3D/2D Viewer",
        categorySlug: "3d-2d-viewer",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-01-25",
        content: `<h2>Color-Coding</h2>
<p>Apply color-coding rules to visualize model data.</p>
<h3>How to Apply</h3>
<ol>
<li>Open <strong>View → Color Coding</strong>.</li>
<li>Select the property to color by (e.g., material, status, phase).</li>
<li>Choose a color scheme or define custom colors.</li>
<li>The model updates in real-time with the applied colors.</li>
</ol>
<p>Color-coding is excellent for QA checks, progress tracking, and identifying missing data.</p>`,
      },
      {
        id: "performance-tips-for-large-models",
        title: "Performance Tips for Large Models",
        category: "3D/2D Viewer",
        categorySlug: "3d-2d-viewer",
        badge: "popular",
        readTime: "4 min",
        lastUpdated: "2026-02-22",
        content: `<h2>Optimizing Performance</h2>
<h3>Tips</h3>
<ul>
<li>Use a modern browser (Chrome recommended).</li>
<li>Enable hardware acceleration in browser settings.</li>
<li>Hide unnecessary elements/categories.</li>
<li>Use section boxes to limit visible geometry.</li>
<li>Close other browser tabs to free memory.</li>
<li>Split very large models into discipline-specific files.</li>
</ul>
<h3>Quality Settings</h3>
<p>Adjust rendering quality in Viewer Settings: Low (fastest), Medium (balanced), High (best quality).</p>`,
      },
      {
        id: "embedding-the-viewer-in-external-sites",
        title: "Embedding the Viewer in External Sites",
        category: "3D/2D Viewer",
        categorySlug: "3d-2d-viewer",
        badge: null,
        readTime: "4 min",
        lastUpdated: "2026-02-18",
        content: `<h2>Embedding the Viewer</h2>
<p>Share interactive 3D views on your website or in presentations.</p>
<h3>Steps</h3>
<ol>
<li>Open the model and navigate to the desired view.</li>
<li>Click <strong>Share → Embed</strong>.</li>
<li>Configure options (allow orbit, show toolbar, auto-rotate).</li>
<li>Copy the embed code (iframe).</li>
<li>Paste it into your website's HTML.</li>
</ol>`,
      },
      {
        id: "federated-model-viewing",
        title: "Federated Model Viewing",
        category: "3D/2D Viewer",
        categorySlug: "3d-2d-viewer",
        badge: null,
        readTime: "5 min",
        lastUpdated: "2026-02-14",
        content: `<h2>Federated Models</h2>
<p>Combine multiple discipline models into a single federated view.</p>
<h3>Creating a Federated View</h3>
<ol>
<li>Open a project with multiple uploaded models.</li>
<li>Click <strong>"Federate"</strong> in the file manager.</li>
<li>Select the models to combine.</li>
<li>Align models using reference points if needed.</li>
<li>Save the federated view.</li>
</ol>
<p>Each discipline model retains its own properties and can be toggled independently within the federated view.</p>`,
      },
      {
        id: "clash-detection-basics",
        title: "Clash Detection Basics",
        category: "3D/2D Viewer",
        categorySlug: "3d-2d-viewer",
        badge: "new",
        readTime: "5 min",
        lastUpdated: "2026-03-06",
        content: `<h2>Clash Detection</h2>
<p>Automatically detect intersections between building elements from different disciplines.</p>
<h3>Running a Clash Test</h3>
<ol>
<li>Open a federated model.</li>
<li>Go to <strong>Tools → Clash Detection</strong>.</li>
<li>Select Set A (e.g., Structural) and Set B (e.g., MEP).</li>
<li>Set tolerance (default: 0mm).</li>
<li>Click <strong>"Run Test"</strong>.</li>
</ol>
<h3>Reviewing Results</h3>
<p>Clashes are listed with automatic viewpoints. Each clash can be assigned as an issue to the responsible team member.</p>`,
      },
      {
        id: "screenshot-and-report-generation",
        title: "Screenshot and Report Generation",
        category: "3D/2D Viewer",
        categorySlug: "3d-2d-viewer",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-02-08",
        content: `<h2>Screenshots & Reports</h2>
<h3>Taking Screenshots</h3>
<p>Click the camera icon to capture the current view. Screenshots include all visible annotations and markups.</p>
<h3>Generating Reports</h3>
<p>Create PDF reports that compile selected viewpoints, annotations, and element data into a professional document.</p>
<ol>
<li>Select viewpoints to include.</li>
<li>Choose report template.</li>
<li>Add cover page and descriptions.</li>
<li>Generate and download the PDF.</li>
</ol>`,
      },
      {
        id: "mini-map-and-floor-plans",
        title: "Mini-Map and Floor Plans",
        category: "3D/2D Viewer",
        categorySlug: "3d-2d-viewer",
        badge: null,
        readTime: "2 min",
        lastUpdated: "2026-02-03",
        content: `<h2>Mini-Map</h2>
<p>The mini-map shows a top-down floor plan view in the corner of the 3D viewer.</p>
<ul>
<li>Click on the mini-map to jump to a location.</li>
<li>The current viewing area is highlighted.</li>
<li>Switch between floors using the floor selector.</li>
</ul>
<p>This feature is especially useful for navigation in large models.</p>`,
      },
      {
        id: "rendering-and-visual-styles",
        title: "Rendering and Visual Styles",
        category: "3D/2D Viewer",
        categorySlug: "3d-2d-viewer",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-01-30",
        content: `<h2>Visual Styles</h2>
<p>Switch between different rendering modes to suit your needs:</p>
<ul>
<li><strong>Shaded</strong> – Full materials and lighting (default).</li>
<li><strong>Wireframe</strong> – Element edges only.</li>
<li><strong>X-Ray</strong> – Semi-transparent view through walls.</li>
<li><strong>Realistic</strong> – Enhanced lighting with shadows and reflections.</li>
<li><strong>Conceptual</strong> – Simplified colors, great for presentations.</li>
</ul>
<p>Combine with section planes and color-coding for maximum clarity.</p>`,
      },
    ],
  },
  {
    slug: "collaboration",
    title: "Collaboration",
    desc: "Share projects, manage team access, and track activity.",
    color: "catGreen",
    articles: [
      {
        id: "setting-up-team-permissions-and-roles",
        title: "Setting Up Team Permissions and Roles",
        category: "Collaboration",
        categorySlug: "collaboration",
        badge: "new",
        readTime: "5 min",
        lastUpdated: "2026-03-04",
        content: `<h2>Team Permissions</h2>
<p>viewStruct provides granular access control to ensure the right people have the right access.</p>
<h3>Permission Levels</h3>
<ul>
<li><strong>Project Admin</strong> – Full control over project settings, members, and data.</li>
<li><strong>Contributor</strong> – Upload files, create/edit issues, and comment.</li>
<li><strong>Viewer</strong> – View-only access to models and documents.</li>
<li><strong>Guest</strong> – Limited access via shared link, no account required.</li>
</ul>
<h3>Managing Permissions</h3>
<ol>
<li>Go to <strong>Project Settings → Members</strong>.</li>
<li>Click on a member to change their role.</li>
<li>Use folder-level permissions for granular control.</li>
</ol>`,
      },
      {
        id: "real-time-collaboration-features",
        title: "Real-Time Collaboration Features",
        category: "Collaboration",
        categorySlug: "collaboration",
        badge: null,
        readTime: "4 min",
        lastUpdated: "2026-02-20",
        content: `<h2>Real-Time Collaboration</h2>
<p>Work together with your team in real-time.</p>
<h3>Features</h3>
<ul>
<li><strong>Live Cursors</strong> – See where other users are looking in the model.</li>
<li><strong>Shared Views</strong> – Navigate to the same viewpoint as a teammate.</li>
<li><strong>Instant Notifications</strong> – Get notified when comments or issues are updated.</li>
<li><strong>Activity Stream</strong> – Track all project changes chronologically.</li>
</ul>`,
      },
      {
        id: "sharing-projects-with-external-stakeholders",
        title: "Sharing Projects with External Stakeholders",
        category: "Collaboration",
        categorySlug: "collaboration",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-02-15",
        content: `<h2>External Sharing</h2>
<p>Share projects with clients, consultants, or other stakeholders without requiring them to create an account.</p>
<h3>Share Links</h3>
<ol>
<li>Click <strong>"Share"</strong> on any project or model.</li>
<li>Choose access level: View Only or Comment.</li>
<li>Optionally set an expiration date and password.</li>
<li>Copy and send the link.</li>
</ol>`,
      },
      {
        id: "comment-and-annotation-workflows",
        title: "Comment and Annotation Workflows",
        category: "Collaboration",
        categorySlug: "collaboration",
        badge: null,
        readTime: "4 min",
        lastUpdated: "2026-02-10",
        content: `<h2>Comments & Annotations</h2>
<p>Leave comments directly on 3D models or 2D sheets for precise communication.</p>
<h3>3D Comments</h3>
<p>Click on a model element → "Add Comment" → Type your message. The comment is pinned to the exact location and viewpoint.</p>
<h3>@Mentions</h3>
<p>Use @username to notify specific team members. They'll receive an email and in-app notification.</p>
<h3>Threading</h3>
<p>Reply to comments to create discussion threads. Mark threads as resolved when addressed.</p>`,
      },
      {
        id: "activity-log-and-audit-trail",
        title: "Activity Log and Audit Trail",
        category: "Collaboration",
        categorySlug: "collaboration",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-02-05",
        content: `<h2>Activity Log</h2>
<p>Every action in your project is logged for transparency and accountability.</p>
<h3>Tracked Activities</h3>
<ul>
<li>File uploads and deletions</li>
<li>Issue creation and status changes</li>
<li>Member additions and role changes</li>
<li>Comments and annotations</li>
<li>Settings modifications</li>
</ul>
<h3>Filtering</h3>
<p>Filter the activity log by date range, user, or action type. Export as CSV for external reporting.</p>`,
      },
      {
        id: "creating-and-managing-groups",
        title: "Creating and Managing Groups",
        category: "Collaboration",
        categorySlug: "collaboration",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-02-01",
        content: `<h2>Groups</h2>
<p>Organize team members into groups for easier permission management.</p>
<h3>Creating Groups</h3>
<ol>
<li>Go to <strong>Organization Settings → Groups</strong>.</li>
<li>Click <strong>"Create Group"</strong>.</li>
<li>Name the group (e.g., "Structural Team", "Client Reviewers").</li>
<li>Add members to the group.</li>
</ol>
<p>Assign project permissions to entire groups instead of individual users.</p>`,
      },
      {
        id: "issue-workflow-customization",
        title: "Issue Workflow Customization",
        category: "Collaboration",
        categorySlug: "collaboration",
        badge: "updated",
        readTime: "5 min",
        lastUpdated: "2026-03-02",
        content: `<h2>Custom Issue Workflows</h2>
<p>Tailor the issue tracking system to match your team's process.</p>
<h3>Custom Statuses</h3>
<p>Add custom statuses beyond the defaults (Open, In Progress, Resolved, Closed). Example: "Awaiting Approval", "On Hold".</p>
<h3>Custom Fields</h3>
<p>Add custom fields to issues: dropdowns, dates, numbers, or text fields specific to your workflow.</p>
<h3>Automation Rules</h3>
<p>Set up rules like: "When status changes to Resolved, notify the issue creator" or "Auto-assign issues tagged 'Structural' to the structural team."</p>`,
      },
      {
        id: "project-templates",
        title: "Project Templates",
        category: "Collaboration",
        categorySlug: "collaboration",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-01-28",
        content: `<h2>Project Templates</h2>
<p>Save time by creating reusable project templates with pre-configured settings.</p>
<h3>What's Included in Templates</h3>
<ul>
<li>Folder structure</li>
<li>Issue categories and workflows</li>
<li>Default team roles</li>
<li>Notification settings</li>
</ul>
<p>Create a template from any existing project via <strong>Project Settings → Save as Template</strong>.</p>`,
      },
      {
        id: "email-notifications-and-digests",
        title: "Email Notifications and Digests",
        category: "Collaboration",
        categorySlug: "collaboration",
        badge: null,
        readTime: "2 min",
        lastUpdated: "2026-01-20",
        content: `<h2>Email Notifications</h2>
<h3>Notification Types</h3>
<ul>
<li><strong>Instant</strong> – Receive an email for each event.</li>
<li><strong>Daily Digest</strong> – A summary of the day's activity, sent each morning.</li>
<li><strong>Weekly Digest</strong> – A weekly summary sent on Mondays.</li>
</ul>
<h3>Customization</h3>
<p>Choose which events trigger notifications per project. Go to <strong>Profile → Notifications</strong> to configure globally, or <strong>Project Settings → Notifications</strong> for per-project settings.</p>`,
      },
    ],
  },
  {
    slug: "security-privacy",
    title: "Security & Privacy",
    desc: "Data protection, encryption, and compliance details.",
    color: "catPurple",
    articles: [
      {
        id: "enabling-two-factor-authentication",
        title: "Enabling Two-Factor Authentication",
        category: "Security & Privacy",
        categorySlug: "security-privacy",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-02-20",
        content: `<h2>Two-Factor Authentication (2FA)</h2>
<p>Add an extra layer of security to your account.</p>
<h3>Setup Steps</h3>
<ol>
<li>Go to <strong>Profile → Security</strong>.</li>
<li>Click <strong>"Enable 2FA"</strong>.</li>
<li>Choose your method: Authenticator App (recommended) or SMS.</li>
<li>Scan the QR code with your authenticator app.</li>
<li>Enter the verification code to confirm.</li>
<li>Save your backup codes in a secure location.</li>
</ol>
<div class="tip"><strong>Important:</strong> Store your backup codes securely. They are the only way to recover your account if you lose access to your authenticator.</div>`,
      },
      {
        id: "data-encryption-and-storage",
        title: "Data Encryption and Storage",
        category: "Security & Privacy",
        categorySlug: "security-privacy",
        badge: null,
        readTime: "4 min",
        lastUpdated: "2026-02-15",
        content: `<h2>Data Encryption</h2>
<h3>In Transit</h3>
<p>All data transmitted between your browser and viewStruct servers is encrypted using TLS 1.3.</p>
<h3>At Rest</h3>
<p>Your files and data are encrypted at rest using AES-256 encryption on our cloud storage systems.</p>
<h3>Data Centers</h3>
<p>viewStruct uses enterprise-grade cloud infrastructure with data centers in multiple regions. You can choose your data residency location during project creation.</p>`,
      },
      {
        id: "gdpr-compliance",
        title: "GDPR Compliance",
        category: "Security & Privacy",
        categorySlug: "security-privacy",
        badge: null,
        readTime: "5 min",
        lastUpdated: "2026-02-10",
        content: `<h2>GDPR Compliance</h2>
<p>viewStruct is fully compliant with the General Data Protection Regulation (GDPR).</p>
<h3>Your Rights</h3>
<ul>
<li><strong>Access</strong> – Request a copy of all your personal data.</li>
<li><strong>Rectification</strong> – Correct inaccurate personal data.</li>
<li><strong>Erasure</strong> – Request deletion of your account and data.</li>
<li><strong>Portability</strong> – Export your data in standard formats.</li>
<li><strong>Restriction</strong> – Limit how we process your data.</li>
</ul>
<p>Contact our Data Protection Officer at <strong>dpo@viewstruct.com</strong> for any GDPR-related requests.</p>`,
      },
      {
        id: "sso-configuration",
        title: "Single Sign-On (SSO) Configuration",
        category: "Security & Privacy",
        categorySlug: "security-privacy",
        badge: "new",
        readTime: "5 min",
        lastUpdated: "2026-03-05",
        content: `<h2>Single Sign-On</h2>
<p>Enterprise customers can configure SSO for centralized authentication.</p>
<h3>Supported Providers</h3>
<ul>
<li>Azure Active Directory</li>
<li>Okta</li>
<li>Google Workspace</li>
<li>Any SAML 2.0 or OIDC provider</li>
</ul>
<h3>Setup</h3>
<ol>
<li>Go to <strong>Organization Settings → Security → SSO</strong>.</li>
<li>Select your identity provider.</li>
<li>Enter the required configuration (Entity ID, SSO URL, Certificate).</li>
<li>Test the connection.</li>
<li>Enable SSO for your organization.</li>
</ol>`,
      },
      {
        id: "password-policy-and-best-practices",
        title: "Password Policy and Best Practices",
        category: "Security & Privacy",
        categorySlug: "security-privacy",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-02-05",
        content: `<h2>Password Security</h2>
<h3>Requirements</h3>
<ul>
<li>Minimum 10 characters</li>
<li>At least one uppercase letter</li>
<li>At least one number</li>
<li>At least one special character</li>
</ul>
<h3>Best Practices</h3>
<ul>
<li>Use a unique password for viewStruct.</li>
<li>Enable two-factor authentication.</li>
<li>Use a password manager.</li>
<li>Never share your credentials.</li>
<li>Change your password if you suspect unauthorized access.</li>
</ul>`,
      },
      {
        id: "session-management",
        title: "Session Management",
        category: "Security & Privacy",
        categorySlug: "security-privacy",
        badge: null,
        readTime: "2 min",
        lastUpdated: "2026-01-30",
        content: `<h2>Session Management</h2>
<p>Control your active sessions across devices.</p>
<h3>Viewing Active Sessions</h3>
<p>Go to <strong>Profile → Security → Active Sessions</strong> to see all devices where you're logged in.</p>
<h3>Session Controls</h3>
<ul>
<li>Revoke individual sessions.</li>
<li>Sign out of all devices at once.</li>
<li>Set session timeout duration (1 hour to 30 days).</li>
</ul>`,
      },
      {
        id: "data-retention-and-deletion",
        title: "Data Retention and Deletion",
        category: "Security & Privacy",
        categorySlug: "security-privacy",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-01-25",
        content: `<h2>Data Retention</h2>
<h3>Active Accounts</h3>
<p>Your data is retained as long as your account is active. Deleted files are moved to trash and permanently deleted after 30 days.</p>
<h3>Account Deletion</h3>
<p>When you delete your account, all personal data is permanently removed within 30 days. Project data shared with others may be retained for team continuity.</p>
<h3>Legal Holds</h3>
<p>Enterprise admins can place legal holds to prevent data deletion during legal proceedings.</p>`,
      },
    ],
  },
  {
    slug: "account-billing",
    title: "Account & Billing",
    desc: "Manage subscriptions, invoices, and account settings.",
    color: "catRed",
    articles: [
      {
        id: "understanding-storage-limits-and-plans",
        title: "Understanding Storage Limits and Plans",
        category: "Account & Billing",
        categorySlug: "account-billing",
        badge: null,
        readTime: "4 min",
        lastUpdated: "2026-02-25",
        content: `<h2>Storage Plans</h2>
<h3>Plan Comparison</h3>
<ul>
<li><strong>Free</strong> – 5GB storage, 2 projects, basic viewer.</li>
<li><strong>Professional</strong> – 100GB storage, unlimited projects, full viewer + collaboration.</li>
<li><strong>Business</strong> – 500GB storage, unlimited projects, advanced analytics + API access.</li>
<li><strong>Enterprise</strong> – Custom storage, dedicated support, SSO, and custom integrations.</li>
</ul>
<h3>Storage Usage</h3>
<p>Monitor your storage usage in <strong>Settings → Storage</strong>. Storage counts include original files and processed data.</p>`,
      },
      {
        id: "upgrading-your-plan",
        title: "Upgrading Your Plan",
        category: "Account & Billing",
        categorySlug: "account-billing",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-02-20",
        content: `<h2>Upgrading</h2>
<ol>
<li>Go to <strong>Settings → Subscription</strong>.</li>
<li>Click <strong>"Upgrade"</strong>.</li>
<li>Select your new plan.</li>
<li>Choose monthly or annual billing (save 20% with annual).</li>
<li>Enter payment details and confirm.</li>
</ol>
<p>Upgrades take effect immediately. You'll be credited for unused time on your current plan.</p>`,
      },
      {
        id: "managing-payment-methods",
        title: "Managing Payment Methods",
        category: "Account & Billing",
        categorySlug: "account-billing",
        badge: null,
        readTime: "2 min",
        lastUpdated: "2026-02-15",
        content: `<h2>Payment Methods</h2>
<h3>Accepted Payment Types</h3>
<ul>
<li>Credit/Debit cards (Visa, Mastercard, AmEx)</li>
<li>PayPal</li>
<li>Bank transfer (Enterprise plans only)</li>
</ul>
<h3>Managing Cards</h3>
<p>Go to <strong>Settings → Billing → Payment Methods</strong> to add, update, or remove payment methods. Your default payment method is charged automatically on each billing cycle.</p>`,
      },
      {
        id: "viewing-and-downloading-invoices",
        title: "Viewing and Downloading Invoices",
        category: "Account & Billing",
        categorySlug: "account-billing",
        badge: null,
        readTime: "2 min",
        lastUpdated: "2026-02-10",
        content: `<h2>Invoices</h2>
<p>Access all your invoices in <strong>Settings → Billing → Invoice History</strong>.</p>
<h3>Features</h3>
<ul>
<li>View detailed invoice breakdown.</li>
<li>Download PDF invoices.</li>
<li>Set up automatic invoice forwarding to your accounting email.</li>
<li>Add company details (VAT number, address) for invoices.</li>
</ul>`,
      },
      {
        id: "canceling-your-subscription",
        title: "Canceling Your Subscription",
        category: "Account & Billing",
        categorySlug: "account-billing",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-02-05",
        content: `<h2>Cancellation</h2>
<h3>How to Cancel</h3>
<ol>
<li>Go to <strong>Settings → Subscription</strong>.</li>
<li>Click <strong>"Cancel Subscription"</strong>.</li>
<li>Select a reason (optional but appreciated).</li>
<li>Confirm cancellation.</li>
</ol>
<h3>What Happens After</h3>
<ul>
<li>Your plan remains active until the end of the billing period.</li>
<li>Your data is retained for 90 days after expiration.</li>
<li>After 90 days, data is permanently deleted.</li>
<li>You can reactivate anytime before data deletion.</li>
</ul>`,
      },
      {
        id: "adding-users-to-your-plan",
        title: "Adding Users to Your Plan",
        category: "Account & Billing",
        categorySlug: "account-billing",
        badge: null,
        readTime: "2 min",
        lastUpdated: "2026-02-01",
        content: `<h2>Adding Users</h2>
<p>Professional and Business plans include a set number of seats. Additional seats can be purchased.</p>
<h3>Pricing</h3>
<ul>
<li><strong>Professional</strong> – $15/month per additional user.</li>
<li><strong>Business</strong> – $25/month per additional user.</li>
</ul>
<p>Go to <strong>Settings → Subscription → Manage Seats</strong> to add or remove user seats. Charges are prorated.</p>`,
      },
      {
        id: "free-trial-information",
        title: "Free Trial Information",
        category: "Account & Billing",
        categorySlug: "account-billing",
        badge: "popular",
        readTime: "2 min",
        lastUpdated: "2026-02-28",
        content: `<h2>Free Trial</h2>
<p>Try viewStruct Professional free for 14 days with full features.</p>
<h3>Trial Includes</h3>
<ul>
<li>100GB storage</li>
<li>Unlimited projects</li>
<li>Full 3D viewer with all tools</li>
<li>Collaboration features</li>
<li>API access</li>
</ul>
<p>No credit card required to start. You'll receive reminders before the trial ends.</p>`,
      },
      {
        id: "tax-vat-information",
        title: "Tax and VAT Information",
        category: "Account & Billing",
        categorySlug: "account-billing",
        badge: null,
        readTime: "2 min",
        lastUpdated: "2026-01-20",
        content: `<h2>Tax & VAT</h2>
<p>Depending on your location, applicable taxes may be added to your subscription.</p>
<h3>Adding VAT Number</h3>
<p>EU business customers can add their VAT number in <strong>Settings → Billing → Tax Info</strong> to apply reverse charge mechanism.</p>
<h3>Tax Receipts</h3>
<p>All invoices include detailed tax information. Contact billing@viewstruct.com for tax-specific questions.</p>`,
      },
      {
        id: "enterprise-plan-features",
        title: "Enterprise Plan Features",
        category: "Account & Billing",
        categorySlug: "account-billing",
        badge: null,
        readTime: "4 min",
        lastUpdated: "2026-02-18",
        content: `<h2>Enterprise Plan</h2>
<h3>Exclusive Features</h3>
<ul>
<li>Custom storage limits</li>
<li>Dedicated account manager</li>
<li>SSO / SAML integration</li>
<li>Custom API rate limits</li>
<li>Priority support (4-hour SLA)</li>
<li>Custom data residency</li>
<li>On-premises deployment option</li>
<li>Advanced audit logs</li>
</ul>
<p>Contact sales@viewstruct.com for a custom quote.</p>`,
      },
      {
        id: "refund-policy",
        title: "Refund Policy",
        category: "Account & Billing",
        categorySlug: "account-billing",
        badge: null,
        readTime: "2 min",
        lastUpdated: "2026-01-15",
        content: `<h2>Refund Policy</h2>
<p>We want you to be satisfied with viewStruct.</p>
<ul>
<li><strong>Monthly plans</strong> – No refunds, but you keep access until the end of the billing period.</li>
<li><strong>Annual plans</strong> – Pro-rated refund available within the first 30 days.</li>
<li><strong>Enterprise plans</strong> – Per contract terms.</li>
</ul>
<p>Contact support@viewstruct.com for refund requests.</p>`,
      },
      {
        id: "changing-your-account-email",
        title: "Changing Your Account Email",
        category: "Account & Billing",
        categorySlug: "account-billing",
        badge: null,
        readTime: "2 min",
        lastUpdated: "2026-01-10",
        content: `<h2>Changing Email</h2>
<ol>
<li>Go to <strong>Profile → Account Settings</strong>.</li>
<li>Click <strong>"Change Email"</strong>.</li>
<li>Enter your new email address.</li>
<li>Verify the new email by clicking the link sent to it.</li>
<li>Your old email will receive a notification of the change.</li>
</ol>
<p>If you use SSO, email changes must be made through your identity provider.</p>`,
      },
    ],
  },
  {
    slug: "integrations-api",
    title: "Integrations & API",
    desc: "Connect with BIM tools, automate workflows, and use APIs.",
    color: "catTeal",
    articles: [
      {
        id: "api-authentication-and-rate-limits",
        title: "API Authentication and Rate Limits",
        category: "Integrations & API",
        categorySlug: "integrations-api",
        badge: "new",
        readTime: "5 min",
        lastUpdated: "2026-03-04",
        content: `<h2>API Authentication</h2>
<h3>Authentication Methods</h3>
<ul>
<li><strong>API Keys</strong> – For server-to-server integration. Generate keys in Settings → API.</li>
<li><strong>OAuth 2.0</strong> – For user-authorized integrations. Supports authorization code and client credentials flows.</li>
<li><strong>Personal Access Tokens</strong> – For scripts and automation.</li>
</ul>
<h3>Rate Limits</h3>
<ul>
<li><strong>Professional</strong> – 100 requests/minute, 10,000/day.</li>
<li><strong>Business</strong> – 500 requests/minute, 50,000/day.</li>
<li><strong>Enterprise</strong> – Custom limits.</li>
</ul>
<p>Rate limit headers are included in every API response.</p>`,
      },
      {
        id: "revit-plugin-setup",
        title: "Revit Plugin Setup",
        category: "Integrations & API",
        categorySlug: "integrations-api",
        badge: null,
        readTime: "4 min",
        lastUpdated: "2026-02-25",
        content: `<h2>Revit Plugin</h2>
<p>Upload models directly from Autodesk Revit to viewStruct.</p>
<h3>Installation</h3>
<ol>
<li>Download the plugin from <strong>Settings → Integrations → Revit</strong>.</li>
<li>Close Revit before installing.</li>
<li>Run the installer and follow the prompts.</li>
<li>Open Revit – the viewStruct tab appears in the ribbon.</li>
</ol>
<h3>Usage</h3>
<ol>
<li>Click <strong>"Publish to viewStruct"</strong> in the ribbon.</li>
<li>Select the project and folder.</li>
<li>Choose export settings (IFC version, included categories).</li>
<li>Click <strong>"Publish"</strong>.</li>
</ol>`,
      },
      {
        id: "webhook-configuration",
        title: "Webhook Configuration",
        category: "Integrations & API",
        categorySlug: "integrations-api",
        badge: null,
        readTime: "4 min",
        lastUpdated: "2026-02-20",
        content: `<h2>Webhooks</h2>
<p>Receive real-time notifications when events occur in viewStruct.</p>
<h3>Available Events</h3>
<ul>
<li>model.uploaded, model.processed</li>
<li>issue.created, issue.updated, issue.closed</li>
<li>comment.added</li>
<li>member.added, member.removed</li>
</ul>
<h3>Setup</h3>
<ol>
<li>Go to <strong>Settings → API → Webhooks</strong>.</li>
<li>Click <strong>"Add Webhook"</strong>.</li>
<li>Enter your endpoint URL.</li>
<li>Select events to subscribe to.</li>
<li>Optionally add a secret for signature verification.</li>
</ol>`,
      },
      {
        id: "power-automate-zapier-integration",
        title: "Power Automate / Zapier Integration",
        category: "Integrations & API",
        categorySlug: "integrations-api",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-02-15",
        content: `<h2>Automation Platforms</h2>
<p>Connect viewStruct with 5000+ apps through Power Automate or Zapier.</p>
<h3>Popular Automations</h3>
<ul>
<li>New issue → Create Jira ticket</li>
<li>File uploaded → Notify Slack channel</li>
<li>Issue closed → Update Excel spreadsheet</li>
<li>New comment → Send Teams message</li>
</ul>
<h3>Setup</h3>
<p>Search for "viewStruct" in Power Automate or Zapier, authorize the connection, and configure your triggers and actions.</p>`,
      },
      {
        id: "rest-api-quick-start",
        title: "REST API Quick Start",
        category: "Integrations & API",
        categorySlug: "integrations-api",
        badge: "popular",
        readTime: "6 min",
        lastUpdated: "2026-02-28",
        content: `<h2>REST API Quick Start</h2>
<h3>Base URL</h3>
<p><code>https://api.viewstruct.com/v1</code></p>
<h3>Authentication</h3>
<p>Include your API key in the header: <code>Authorization: Bearer YOUR_API_KEY</code></p>
<h3>Example: List Projects</h3>
<pre><code>GET /v1/projects
Authorization: Bearer YOUR_API_KEY</code></pre>
<h3>Example: Upload a File</h3>
<pre><code>POST /v1/projects/{project_id}/files
Content-Type: multipart/form-data
Authorization: Bearer YOUR_API_KEY</code></pre>
<p>See our full API documentation at <strong>docs.viewstruct.com/api</strong>.</p>`,
      },
      {
        id: "bim-360-data-migration",
        title: "BIM 360 Data Migration",
        category: "Integrations & API",
        categorySlug: "integrations-api",
        badge: null,
        readTime: "5 min",
        lastUpdated: "2026-02-10",
        content: `<h2>Migrating from BIM 360</h2>
<h3>What Can Be Migrated</h3>
<ul>
<li>Project structure and folders</li>
<li>All uploaded files and models</li>
<li>Issues (with status, assignees, and comments)</li>
<li>Team members and permissions</li>
</ul>
<h3>Migration Steps</h3>
<ol>
<li>Go to <strong>Settings → Import → BIM 360</strong>.</li>
<li>Authorize access to your Autodesk account.</li>
<li>Select projects to migrate.</li>
<li>Review the migration plan.</li>
<li>Start migration (runs in the background).</li>
</ol>
<p>Large projects may take several hours to complete migration.</p>`,
      },
      {
        id: "graphql-api-overview",
        title: "GraphQL API Overview",
        category: "Integrations & API",
        categorySlug: "integrations-api",
        badge: "new",
        readTime: "5 min",
        lastUpdated: "2026-03-06",
        content: `<h2>GraphQL API</h2>
<p>For more flexible queries, use our GraphQL endpoint.</p>
<h3>Endpoint</h3>
<p><code>https://api.viewstruct.com/graphql</code></p>
<h3>Example Query</h3>
<pre><code>query {
  projects(first: 10) {
    id
    name
    modelsCount
    issues(status: OPEN) {
      id
      title
      priority
    }
  }
}</code></pre>
<p>The GraphQL Playground is available at <strong>api.viewstruct.com/graphql/playground</strong>.</p>`,
      },
      {
        id: "slack-integration",
        title: "Slack Integration",
        category: "Integrations & API",
        categorySlug: "integrations-api",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-02-08",
        content: `<h2>Slack Integration</h2>
<p>Get viewStruct notifications directly in your Slack channels.</p>
<h3>Setup</h3>
<ol>
<li>Go to <strong>Settings → Integrations → Slack</strong>.</li>
<li>Click <strong>"Connect to Slack"</strong>.</li>
<li>Authorize the viewStruct Slack app.</li>
<li>Choose which channel receives notifications.</li>
<li>Configure which events are posted.</li>
</ol>
<h3>Slash Commands</h3>
<ul>
<li><code>/viewstruct projects</code> – List your projects.</li>
<li><code>/viewstruct issues [project]</code> – List open issues.</li>
<li><code>/viewstruct upload</code> – Quick file upload link.</li>
</ul>`,
      },
      {
        id: "teams-integration",
        title: "Microsoft Teams Integration",
        category: "Integrations & API",
        categorySlug: "integrations-api",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-02-05",
        content: `<h2>Microsoft Teams Integration</h2>
<p>Bring viewStruct updates into your Teams workflow.</p>
<h3>Features</h3>
<ul>
<li>Notifications in Teams channels</li>
<li>Tab integration – view models directly in Teams</li>
<li>Bot commands for quick actions</li>
<li>Adaptive cards for rich issue previews</li>
</ul>
<h3>Setup</h3>
<p>Install the viewStruct app from the Teams App Store and follow the authorization flow.</p>`,
      },
      {
        id: "custom-integration-development",
        title: "Custom Integration Development",
        category: "Integrations & API",
        categorySlug: "integrations-api",
        badge: null,
        readTime: "5 min",
        lastUpdated: "2026-02-01",
        content: `<h2>Custom Integrations</h2>
<p>Build custom integrations using our comprehensive APIs.</p>
<h3>SDKs Available</h3>
<ul>
<li>JavaScript/TypeScript</li>
<li>Python</li>
<li>C# / .NET</li>
</ul>
<h3>Getting Started</h3>
<ol>
<li>Register your application in <strong>Settings → API → Applications</strong>.</li>
<li>Choose OAuth 2.0 or API key authentication.</li>
<li>Install the SDK: <code>npm install @viewstruct/sdk</code></li>
<li>Refer to docs.viewstruct.com for complete API reference.</li>
</ol>`,
      },
      {
        id: "navisworks-export-support",
        title: "Navisworks Export Support",
        category: "Integrations & API",
        categorySlug: "integrations-api",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-01-28",
        content: `<h2>Navisworks Export</h2>
<p>Export viewStruct data for use in Autodesk Navisworks.</p>
<h3>Export Options</h3>
<ul>
<li><strong>NWC</strong> – Cache file format for quick loading.</li>
<li><strong>BCF</strong> – Export issues and viewpoints as BCF files.</li>
<li><strong>IFC</strong> – Re-export processed models as IFC.</li>
</ul>
<p>Go to <strong>File Manager → Select File → Export</strong> to choose your format.</p>`,
      },
      {
        id: "archicad-integration",
        title: "ArchiCAD Integration",
        category: "Integrations & API",
        categorySlug: "integrations-api",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-01-25",
        content: `<h2>ArchiCAD Integration</h2>
<p>Publish models from Graphisoft ArchiCAD to viewStruct.</p>
<h3>Setup</h3>
<ol>
<li>Download the viewStruct add-on from our Integrations page.</li>
<li>Install the add-on in ArchiCAD.</li>
<li>Use <strong>File → Publish to viewStruct</strong>.</li>
<li>Select IFC export settings optimized for viewStruct.</li>
</ol>`,
      },
      {
        id: "tekla-structures-integration",
        title: "Tekla Structures Integration",
        category: "Integrations & API",
        categorySlug: "integrations-api",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-01-20",
        content: `<h2>Tekla Structures</h2>
<p>Export structural models from Tekla Structures to viewStruct for coordination.</p>
<h3>Export Steps</h3>
<ol>
<li>In Tekla, go to <strong>Export → IFC</strong>.</li>
<li>Select IFC 4 format for best compatibility.</li>
<li>Export and upload to viewStruct.</li>
</ol>
<p>viewStruct preserves Tekla's structural element properties and classifications.</p>`,
      },
      {
        id: "procore-integration",
        title: "Procore Integration",
        category: "Integrations & API",
        categorySlug: "integrations-api",
        badge: null,
        readTime: "3 min",
        lastUpdated: "2026-01-15",
        content: `<h2>Procore Integration</h2>
<p>Sync issues and documents between viewStruct and Procore.</p>
<h3>Sync Options</h3>
<ul>
<li><strong>One-way</strong> – viewStruct → Procore or Procore → viewStruct.</li>
<li><strong>Two-way</strong> – Bi-directional sync of issues and statuses.</li>
</ul>
<h3>Setup</h3>
<ol>
<li>Go to <strong>Settings → Integrations → Procore</strong>.</li>
<li>Authorize your Procore account.</li>
<li>Map viewStruct projects to Procore projects.</li>
<li>Configure sync direction and field mapping.</li>
</ol>`,
      },
    ],
  },
];

export const allArticles = categories.flatMap((cat) => cat.articles);

export const videos = [
  {
    id: "vid-1",
    title: "Platform Overview",
    duration: "5:32",
    url: "https://www.youtube.com/embed/Q1HHlLMr-q0",
    description:
      "A comprehensive tour of viewStruct, covering the dashboard, file manager, 3D viewer, and collaboration features.",
  },
  {
    id: "vid-2",
    title: "Uploading Your First Model",
    duration: "3:18",
    url: "https://www.youtube.com/embed/OBi0RvPhMIs",
    description:
      "Step-by-step guide to uploading IFC, Revit, and DWG files to viewStruct.",
  },
  {
    id: "vid-3",
    title: "Collaboration & Markups",
    duration: "7:45",
    url: "https://www.youtube.com/embed/I23DRn92Gvs?si=3-A-JtAQs9BC79XN",
    description:
      "Learn how to use comments, annotations, and issue tracking for effective team collaboration.",
  },
  {
    id: "vid-4",
    title: "Model Comparison Tool",
    duration: "4:22",
    url: "",
    description:
      "Compare different versions of your BIM model to track changes over time.",
  },
  {
    id: "vid-5",
    title: "Clash Detection Workflow",
    duration: "6:10",
    url: "",
    description:
      "Run clash tests between discipline models and assign issues to team members.",
  },
  {
    id: "vid-6",
    title: "API & Integrations Setup",
    duration: "8:15",
    url: "",
    description:
      "Connect viewStruct with Revit, Slack, and other tools using our API and plugins.",
  },
];

export function getCategoryBySlug(slug) {
  return categories.find((c) => c.slug === slug);
}

export function getArticleById(id) {
  return allArticles.find((a) => a.id === id);
}

export function searchArticles(query) {
  const q = query.toLowerCase();
  return allArticles.filter(
    (a) =>
      a.title.toLowerCase().includes(q) ||
      a.category.toLowerCase().includes(q) ||
      a.content.toLowerCase().includes(q),
  );
}
